import React from 'react';
import { createRoot } from 'react-dom/client';
import { ExportOutlined } from '@ant-design/icons';
import L from 'leaflet';
import JSZip from 'jszip'; // Import JSZip for zipping files
import FileSaver from 'file-saver'; // Import FileSaver for file download

type LayerMetadata = any;

class ExportControl extends L.Control {
  iconElement = React.createElement(ExportOutlined, { style: { fontSize: '16px' } });

  onAdd(map: L.Map): HTMLElement {
    const container = L.DomUtil.create('div', 'leaflet-bar');
    container.id = 'mapCustomExportLayersId';
    const button = L.DomUtil.create('a', '', container);
    button.href = '#';
    button.title = 'Export Layers';
    const iconElement = React.createElement(ExportOutlined, { style: { fontSize: '16px' } });

    const root = createRoot(button); // Initialize React root
    root.render(iconElement);

    L.DomEvent.on(button, 'click', (e: Event) => {
      e.preventDefault();
      this.exportLayers(map);
    });

    return container;
  }

  // Function to export layers as a ZIP file
  private async exportLayers(map: L.Map): Promise<void> {
    const layersToExport: LayerMetadata[] = []; // Collect layers to export

    // Create a new JSZip instance
    const zip = new JSZip();
    let index = 0;
    // Loop through all layers in the map and filter the layers you want to export
    map.eachLayer((layer: L.Layer) => {
      if (layer instanceof L.GeoJSON) {
        // If the layer is a GeoJSON layer, export its features
        const geoJson = layer.toGeoJSON();
        if (geoJson.type === 'FeatureCollection') {
          layersToExport.push({
            type: 'GeoJSON Layer',
            geoJson,
          });

          // Add the GeoJSON data to the ZIP file
          const geoJsonFileName = `geojson-layer-${index}.geojson`;
          zip.file(geoJsonFileName, JSON.stringify(geoJson));
        }
      } else if (layer instanceof L.TileLayer.WMS) {
        // If the layer is a WMS layer, export its URL and parameters
        const wmsLayer: any = layer;
        const url = wmsLayer._url;
        if (url) {
          layersToExport.push({
            type: 'WMS Layer',
            url: url,
            params: wmsLayer.wmsParams,
          });

          // Create a text file for the WMS URL and parameters and add to ZIP
          const wmsFileName = `wms-layer-${index}.txt`;
          const wmsContent = `URL: ${url}\nParameters: ${JSON.stringify(wmsLayer.wmsParams)}`;
          zip.file(wmsFileName, wmsContent);
        }
      } else if (layer instanceof L.Layer) {
        // If it's a custom layer or other type of layer, check if _url exists
        const customLayer: any = layer;
        const url = customLayer._url;
        if (url) {
          layersToExport.push({
            type: 'Custom Layer',
            url: url,
            params: customLayer.options,
          });

          // Create a text file for custom layer options and add to ZIP
          const customLayerFileName = `custom-layer-${index}.txt`;
          const customLayerContent = `URL: ${url}\nOptions: ${JSON.stringify(customLayer.options)}`;
          zip.file(customLayerFileName, customLayerContent);
        }
      }
      index++;
    });

    // Generate the ZIP file and trigger the download
    zip.generateAsync({ type: 'blob' }).then((content) => {
      FileSaver.saveAs(content, 'exported-layers.zip');
    });
  }
}
export default ExportControl;
